import {
  CheckCircle,
  DeleteOutline,
  EditOutlined,
  ExpandMore,
  LogoutOutlined,
  PlaylistAdd,
  PhotoCamera,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import AlertPopup from "../../components/common/alertPopup/AlertPopup";
import {
  getInventoryCrewDetailsAction,
  getCrewSurveyItemsAction,
  setInventoryNoteUpdatedAction,
  setUpdateInventoryCrewDetailsAction,
  uploadCrewInventoryPhotoAction,
  updateInventoryCrewDetailsAction,
} from "../../store/reducers/inventoryCrewSlice";
import {
  CREW_ITEM_TABLE_HEADINGS,
  CrewInventoryFormData,
  CrewInventoryItemFormData,
  CrewInventoryRequiredField,
  InventoryPayloadItem,
  crewItemFormFields,
  crewSignatureFields,
  initialCrewInventoryFormData,
  initialItemFormData,
} from "../../clientAdmin/components/storage/components/inventory/utils";
import FormField from "../../components/common/FormField";
import CrewInventoryNoteModal from "../components/CrewInventoryNoteModal";
import Notification from "../../components/common/notification/Notification";

const formatAddress = (address: any[] | null | undefined): string => {
  if (!Array.isArray(address) || address.length === 0) {
    return "-";
  }

  return address
    .flatMap((item) => [
      item?.street,
      item?.street_2,
      item?.city,
      item?.town,
      item?.county,
      item?.postcode,
      item?.country,
    ])
    .filter((value) => value !== null && value !== undefined && value !== "")
    .join(", ");
};

const getInventoryItemKey = (item: InventoryPayloadItem): string =>
  item.survey_item_id
    ? `survey:${item.survey_item_id}`
    : item.id
      ? `inventory:${item.id}`
      : `${item.title}:${item.quantity}:${item.condition_code}`;

const mapSurveyItem = (item: any): InventoryPayloadItem => ({
  survey_item_id: item.id ? String(item.id) : null,
  title:
    item.title ??
    item.item ??
    item.item_name ??
    item.article ??
    item.name ??
    "",
  quantity: Number(item.quantity ?? item.qty ?? 1),
  condition_code: item.condition_code ?? item.condition ?? "PBO",
  photo_key: item.photo_key ?? "",
  status: item.status ?? "PACKED",
  notes: item.notes ?? "",
});

const CrewInventoryPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [preloadRequested, setPreloadRequested] = useState(false);
  const contractId = searchParams.get("contract_id");
  const {
    clientInventoryCrewDetails,
    isInventoryCrewDetailsUpdated,
    isInventoryNoteUpdated,
    crewInventoryPhotoUpload,
    crewSurveyItems,
    isCrewSurveyItemsLoading,
  } = useSelector((state: any) => state.inventoryCrew);
  const [activeTab, setActiveTab] = useState<"capture" | "verification">(
    "capture",
  );
  const [inventoryFormData, setInventoryFormData] =
    useState<CrewInventoryFormData>(initialCrewInventoryFormData);
  const [itemFormData, setItemFormData] =
    useState<CrewInventoryItemFormData>(initialItemFormData);
  const [itemPhoto, setItemPhoto] = useState<File | null>(null);
  const [photoUrlByKey, setPhotoUrlByKey] = useState<Record<string, string>>(
    {},
  );
  const [editingItem, setEditingItem] = useState<InventoryPayloadItem | null>(
    null,
  );
  const [captureHasUnsavedChanges, setCaptureHasUnsavedChanges] =
    useState(false);
  const [tabSwitchTarget, setTabSwitchTarget] = useState<
    "capture" | "verification" | null
  >(null);
  const [formErrors, setFormErrors] = useState<
    Partial<Record<CrewInventoryRequiredField, string>>
  >({});
  const resetCaptureAfterRefresh = useRef(false);
  const preserveFieldsAfterRefresh = useRef(false);
  const discardPhotoUploadRef = useRef(false);
  const [selectedVerificationItem, setSelectedVerificationItem] =
    useState<InventoryPayloadItem | null>(null);
  const [pendingVerificationItems, setPendingVerificationItems] = useState<
    InventoryPayloadItem[]
  >([]);
  const inventoryStatus = String(
    clientInventoryCrewDetails?.status ??
      clientInventoryCrewDetails?.inventory?.status ??
      inventoryFormData.status ??
      "",
  ).toUpperCase();

  const isValidInventoryItem = (item: InventoryPayloadItem): boolean =>
    Number.isFinite(Number(item.quantity)) && Number(item.quantity) > 0;

  const isSubmitted =
    inventoryStatus === "SUBMITTED" || inventoryStatus === "COMPLETED";
  const isCompleted = inventoryStatus === "COMPLETED";
  const inventoryId = clientInventoryCrewDetails?.inventory?.id;

  const handleLogout = () => {
    const emailEntryUrl = `/crew-inventory${location.search}`;
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace(emailEntryUrl);
  };

 const handlePreloadSurveyItems = () => {
  if (inventoryId && !isCrewSurveyItemsLoading) {
    setPreloadRequested(true);
    dispatch(getCrewSurveyItemsAction({ inventoryId, role: "crew" }));
  }
};

 useEffect(() => {
  if (
    !preloadRequested ||
    isCrewSurveyItemsLoading ||
    !Array.isArray(crewSurveyItems)
  ) {
    return;
  }

  // Existing inventory items ke survey_item_id collect karein.
  const existingSurveyIds = new Set(
    inventoryFormData.items
      .map((item) => item.survey_item_id)
      .filter((id): id is string => Boolean(id))
      .map(String),
  );

  // Survey API ke item.id ko saved inventory ke survey_item_id se match karein.
  const newItems = crewSurveyItems
    .filter((item: any) => item?.id !== null && item?.id !== undefined)
    .map(mapSurveyItem)
    .filter(isValidInventoryItem)
    .filter(
      (item) =>
        item.survey_item_id &&
        !existingSurveyIds.has(String(item.survey_item_id)),
    );

  if (newItems.length > 0) {
    setCaptureHasUnsavedChanges(true);

    setInventoryFormData((previous) => {
      // Functional update mein dobara duplicate check.
      const previousSurveyIds = new Set(
        previous.items
          .map((item) => item.survey_item_id)
          .filter((id): id is string => Boolean(id))
          .map(String),
      );

      const stillNewItems = newItems.filter(
        (item) =>
          item.survey_item_id &&
          !previousSurveyIds.has(String(item.survey_item_id)),
      );

      return {
        ...previous,
        items: [
          ...previous.items.filter(isValidInventoryItem),
          ...stillNewItems.map((item, index) => ({
            ...item,
            ref_id: String(previous.items.length + index + 1),
          })),
        ],
      };
    });

    setItemFormData((previous) => ({
      ...previous,
      ref_id: String(
        inventoryFormData.items.filter(isValidInventoryItem).length +
          newItems.length +
          1,
      ),
    }));
  }

  setPreloadRequested(false);
}, [
  preloadRequested,
  isCrewSurveyItemsLoading,
  crewSurveyItems,
  inventoryFormData.items,
]);

  const handleItemFieldChange = (event: any) => {
    const { id, name, value } = event.target;
    const fieldId = (id || name) as
      | keyof CrewInventoryItemFormData
      | keyof CrewInventoryFormData;
    setCaptureHasUnsavedChanges(true);

    if (fieldId === "title" || fieldId === "quantity") {
      let error = "";
      if (fieldId === "title" && !value.trim()) {
        error = "Item name is required.";
      } else if (
        fieldId === "quantity" &&
        (!Number.isFinite(Number(value)) || Number(value) < 1)
      ) {
        error = "Quantity must be at least 1.";
      }
      setFormErrors((previous) => ({ ...previous, [fieldId]: error }));
    }

    if (fieldId === "driver_sign_pickup" && value.trim()) {
      setFormErrors((previous) => ({
        ...previous,
        driver_sign_pickup: undefined,
      }));
    }

    if (fieldId === "customer_sign" || fieldId === "driver_sign_pickup") {
      setInventoryFormData((previous) => ({ ...previous, [fieldId]: value }));
    } else {
      setItemFormData((previous) => ({ ...previous, [fieldId]: value }));
    }
  };

  const handleItemPhotoChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedPhoto = event.target.files?.[0] || null;
    setItemPhoto(selectedPhoto);
    setItemFormData((previous) => ({
      ...previous,
      photo_key: "",
    }));
    if (selectedPhoto) {
      discardPhotoUploadRef.current = false;
      setCaptureHasUnsavedChanges(true);
      dispatch(uploadCrewInventoryPhotoAction({ file: selectedPhoto }));
    }
    event.target.value = "";
  };

  useEffect(() => {
    if (
      crewInventoryPhotoUpload?.key &&
      !crewInventoryPhotoUpload.isUploading
    ) {
      if (discardPhotoUploadRef.current) {
        discardPhotoUploadRef.current = false;
        return;
      }
      setItemFormData((previous) => ({
        ...previous,
        photo_key: crewInventoryPhotoUpload.key,
      }));
      if (crewInventoryPhotoUpload.url) {
        setPhotoUrlByKey((previous) => ({
          ...previous,
          [crewInventoryPhotoUpload.key]: crewInventoryPhotoUpload.url,
        }));
      }
    }
  }, [
    crewInventoryPhotoUpload?.key,
    crewInventoryPhotoUpload?.isUploading,
    crewInventoryPhotoUpload?.url,
  ]);

  const getPhotoUrl = (photoKey: string) =>
    photoUrlByKey[photoKey] || (/^https?:\/\//i.test(photoKey) ? photoKey : "");

  const handleViewPhoto = (photoKey: string) => {
    const photoUrl = getPhotoUrl(photoKey);
    if (photoUrl) {
      window.open(photoUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleRemoveItemPhoto = () => {
    setItemPhoto(null);
    setItemFormData((previous) => ({ ...previous, photo_key: "" }));
    setEditingItem((previous) =>
      previous ? { ...previous, photo_key: "" } : previous,
    );
    setCaptureHasUnsavedChanges(true);
  };

  const createCurrentItem = (): InventoryPayloadItem => ({
    ...(editingItem || {}),
    title: itemFormData.title.trim(),
    quantity: Number(itemFormData.quantity),
    condition_code: itemFormData.condition_code,
    photo_key: itemFormData.photo_key,
    status: "PACKED",
    notes: "",
  });

  const submitInventory = (status: "DRAFT" | "SUBMITTED") => {
    if (!contractId) {
      return;
    }

    const nextErrors: Partial<Record<CrewInventoryRequiredField, string>> = {};
    const validExistingItems =
      inventoryFormData.items.filter(isValidInventoryItem);

    const hasItemEntry = Boolean(
      editingItem ||
      itemPhoto ||
      itemFormData.title.trim() ||
      itemFormData.quantity !== initialItemFormData.quantity ||
      itemFormData.condition_code !== initialItemFormData.condition_code ||
      itemFormData.photo_key,
    );

    // Validate current form only if it has an entry or no valid table items exist.
    const shouldSaveCurrentItem =
      hasItemEntry || validExistingItems.length === 0;

    if (shouldSaveCurrentItem) {
      if (!itemFormData.title.trim()) {
        nextErrors.title = "Item name is required.";
      }

      if (
        !Number.isFinite(Number(itemFormData.quantity)) ||
        Number(itemFormData.quantity) < 1
      ) {
        nextErrors.quantity = "Quantity must be at least 1.";
      }
    }
    // Driver sign is mandatory only for Submit & Lock.
    if (
      status === "SUBMITTED" &&
      !inventoryFormData.driver_sign_pickup.trim()
    ) {
      nextErrors.driver_sign_pickup = "Driver sign is required to submit.";
    }

    setFormErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    let nextItems = [...validExistingItems];

    if (shouldSaveCurrentItem) {
      const currentItem = createCurrentItem();

      if (editingItem) {
        nextItems = nextItems.map((item) =>
          getInventoryItemKey(item) === getInventoryItemKey(editingItem)
            ? currentItem
            : item,
        );
      } else {
        nextItems.push(currentItem);
      }
    }

    dispatch(
      updateInventoryCrewDetailsAction({
        inventoryId: clientInventoryCrewDetails?.inventory?.id || "",
        contractId,
        role: "crew",
        inventoryData: {
          ...inventoryFormData,
          status,
          items: nextItems.filter(isValidInventoryItem),
        },
      }),
    );
  };

  const handleEditItem = (item: InventoryPayloadItem, refId: number) => {
    setEditingItem(item);
    setItemFormData({
      ref_id: String(refId),
      title: item.title,
      quantity: String(item.quantity),
      condition_code: item.condition_code,
      photo_key: item.photo_key,
    });
    setItemPhoto(null);
    setCaptureHasUnsavedChanges(true);
  };

  const handleCancelItemEdit = () => {
    setEditingItem(null);
    setItemFormData((previous) => ({
      ...initialItemFormData,
      ref_id: previous.ref_id,
    }));
    setItemPhoto(null);
    setCaptureHasUnsavedChanges(false);
  };

  const handleTabChange = (nextTab: "capture" | "verification") => {
    if (nextTab === activeTab) {
      return;
    }
    if (nextTab === "verification" && !isSubmitted) {
      return;
    }

    const hasUnsavedChanges =
      activeTab === "capture"
        ? captureHasUnsavedChanges
        : pendingVerificationItems.length > 0;

    if (hasUnsavedChanges) {
      setTabSwitchTarget(nextTab);
      return;
    }

    setActiveTab(nextTab);
  };

  const handleDiscardAndSwitchTab = () => {
    if (!tabSwitchTarget) {
      return;
    }

    if (activeTab === "capture") {
      discardPhotoUploadRef.current = Boolean(
        crewInventoryPhotoUpload?.isUploading,
      );
      const savedInventory = clientInventoryCrewDetails?.inventory || {};
      setItemFormData((previous) => ({
        ...initialItemFormData,
        ref_id: previous.ref_id,
      }));
      setItemPhoto(null);
      setEditingItem(null);
      setCaptureHasUnsavedChanges(false);
      setFormErrors({});
      setInventoryFormData((previous) => ({
        ...previous,
        customer_sign:
          clientInventoryCrewDetails?.customer_sign ??
          savedInventory.customer_sign ??
          "",
        driver_sign_pickup:
          clientInventoryCrewDetails?.driver_sign_pickup ??
          savedInventory.driver_sign_pickup ??
          "",
        driver_sign_delivery:
          clientInventoryCrewDetails?.driver_sign_delivery ??
          savedInventory.driver_sign_delivery ??
          "",
      }));
    } else {
      setPendingVerificationItems([]);
      setSelectedVerificationItem(null);
      const savedInventory = clientInventoryCrewDetails?.inventory || {};
      setInventoryFormData((previous) => ({
        ...previous,
        driver_sign_delivery:
          clientInventoryCrewDetails?.driver_sign_delivery ??
          savedInventory.driver_sign_delivery ??
          "",
      }));
    }

    setActiveTab(tabSwitchTarget);
    setTabSwitchTarget(null);
  };

  const handleVerificationNoteSave = (values: {
    notes: string;
    driver_sign_delivery: string;
    status: string;
  }) => {
    if (isCompleted || !selectedVerificationItem) {
      return;
    }

    const updatedItem = {
      ...selectedVerificationItem,
      notes: values.notes,
      status: values.status,
    };

    setPendingVerificationItems((previous) => [
      ...previous.filter(
        (item) =>
          getInventoryItemKey(item) !== getInventoryItemKey(updatedItem),
      ),
      updatedItem,
    ]);
    setInventoryFormData((previous) => ({
      ...previous,
      driver_sign_delivery: values.driver_sign_delivery,
    }));
    setSelectedVerificationItem(null);
  };

  const handleSaveProgress = () => {
    if (!contractId || isCompleted || pendingVerificationItems.length === 0) {
      return;
    }

    dispatch(
      updateInventoryCrewDetailsAction({
        inventoryId: clientInventoryCrewDetails?.inventory?.id || "",
        contractId,
        role: "crew",
        isVerificationNoteUpdate: true,
        inventoryData: {
          ...inventoryFormData,
          status: "SUBMITTED",
          items: pendingVerificationItems.filter(
            (item) => Number(item.quantity) > 0,
          ),
        },
      }),
    );
  };

  const handleCompleteAndSign = () => {
    if (!contractId || !inventoryId || !isSubmitted || isCompleted) {
      return;
    }

    const pendingItemsByKey = new Map(
      pendingVerificationItems.map((item) => [getInventoryItemKey(item), item]),
    );
    const items = inventoryFormData.items
      .map((item) => pendingItemsByKey.get(getInventoryItemKey(item)) ?? item)
      .filter((item) => Number(item.quantity) > 0);

    dispatch(
      updateInventoryCrewDetailsAction({
        inventoryId,
        contractId,
        role: "crew",
        isVerificationNoteUpdate: true,
        inventoryData: {
          ...inventoryFormData,
          status: "COMPLETED",
          items,
        },
      }),
    );
  };

  const handleVerificationItemClick = (item: any) => {
    if (isCompleted) {
      return;
    }

    const selectedItem: InventoryPayloadItem = {
      id: item.id,
      survey_item_id: item.survey_item_id
    ? String(item.survey_item_id)
    : null,
      title: item.title ?? item.item ?? "",
      quantity: Number(item.quantity ?? item.qty ?? 0),
      condition_code: item.condition_code ?? item.condition ?? "",
      photo_key: item.photo_key ?? "",
      status: item.status ?? "DRAFT",
      notes: item.notes ?? "",
    };
    const itemKey = getInventoryItemKey(selectedItem);
    setSelectedVerificationItem(
      pendingVerificationItems.find(
        (pendingItem) => getInventoryItemKey(pendingItem) === itemKey,
      ) ?? selectedItem,
    );
  };

  useEffect(() => {
    if (contractId) {
      dispatch(getInventoryCrewDetailsAction({ contractId, role: "crew" }));
    }
  }, [dispatch, contractId, activeTab]);

  useEffect(() => {
    if (clientInventoryCrewDetails) {
      const shouldResetCapture = resetCaptureAfterRefresh.current;
      const shouldPreserveFields = preserveFieldsAfterRefresh.current;
      const responseItems = clientInventoryCrewDetails.items || [];
      const inventory = clientInventoryCrewDetails.inventory || {};

     const items: InventoryPayloadItem[] = responseItems
  .map((item: any) => ({
    id: String(item.id ?? ""),
    survey_item_id: item.survey_item_id
      ? String(item.survey_item_id)
      : null,
    title: item.title ?? item.item ?? "",
    quantity: Number(item.quantity ?? item.qty ?? 0),
    condition_code: item.condition_code ?? item.condition ?? "",
    photo_key: item.photo_key ?? "",
    status: item.status ?? "DRAFT",
    notes: item.notes ?? "",
  }))
  .filter(isValidInventoryItem);

      setPhotoUrlByKey((previous) => ({
        ...previous,
        ...Object.fromEntries(
          responseItems
            .filter(
              (item: any) =>
                item.photo_key && (item.photo_url || item.photoUrl || item.url),
            )
            .map((item: any) => [
              item.photo_key,
              item.photo_url || item.photoUrl || item.url,
            ]),
        ),
      }));
      const receivedRefId =
        clientInventoryCrewDetails.ref_id ??
        inventory.ref_id ??
        responseItems.length + 1;

      setInventoryFormData((previous) => ({
        status:
          clientInventoryCrewDetails.status ??
          inventory.status ??
          initialCrewInventoryFormData.status,
        customer_sign:
          clientInventoryCrewDetails.customer_sign ||
          inventory.customer_sign ||
          previous.customer_sign ||
          "",
        driver_sign_pickup:
          clientInventoryCrewDetails.driver_sign_pickup ||
          inventory.driver_sign_pickup ||
          previous.driver_sign_pickup ||
          "",
        driver_sign_delivery:
          clientInventoryCrewDetails.driver_sign_delivery ??
          inventory.driver_sign_delivery ??
          "",
        items,
      }));
      setItemFormData((previous) => ({
        ...(shouldPreserveFields ? previous : initialItemFormData),
        ref_id: String(receivedRefId),
      }));
      if (shouldResetCapture) {
        resetCaptureAfterRefresh.current = false;
        setFormErrors({});
        setItemPhoto(null);
      }
      if (shouldPreserveFields) {
        preserveFieldsAfterRefresh.current = false;
      }
    }
  }, [clientInventoryCrewDetails]);

  useEffect(() => {
    if (isInventoryCrewDetailsUpdated) {
      resetCaptureAfterRefresh.current = true;
      setItemFormData((previous) => ({
        ...initialItemFormData,
        ref_id: previous.ref_id,
      }));
      setInventoryFormData((previous) => ({
        ...previous,
        status: "DRAFT",
      }));
      setFormErrors({});
      setItemPhoto(null);
      setEditingItem(null);
      setCaptureHasUnsavedChanges(false);
      dispatch(getInventoryCrewDetailsAction({ contractId, role: "crew" }));
      dispatch(setUpdateInventoryCrewDetailsAction(false));
    }
  }, [contractId, dispatch, isInventoryCrewDetailsUpdated]);

  useEffect(() => {
    if (isInventoryNoteUpdated) {
      preserveFieldsAfterRefresh.current = true;
      setPendingVerificationItems([]);
      setSelectedVerificationItem(null);
      dispatch(getInventoryCrewDetailsAction({ contractId, role: "crew" }));
      dispatch(setInventoryNoteUpdatedAction(false));
    }
  }, [contractId, dispatch, isInventoryNoteUpdated]);

  const inventory = clientInventoryCrewDetails?.inventory;

  const inventoryMeta = [
    {
      label: "Job ID",
      value: clientInventoryCrewDetails?.lead_id || "-",
    },
    {
      label: "Customer",
      value: clientInventoryCrewDetails?.customer_name || "-",
    },
    {
      label: "Origin",
      value: formatAddress(clientInventoryCrewDetails?.collection_address),
    },
    {
      label: "Destination",
      value: formatAddress(clientInventoryCrewDetails?.delivery_address),
    },
    {
      label: "Date",
      value: inventory?.created_at
        ? new Date(inventory.created_at).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "-",
    },
    {
      label: "Tag",
      value:
        inventoryFormData.items.length > 0
          ? `1 - ${inventoryFormData.items.length}`
          : "-",
    },
  ];

  const displayItems = inventoryFormData.items
    .map((item, originalIndex) => ({
      item,
      originalIndex,
    }))
    .filter(({ item }) => isValidInventoryItem(item))
    .map(({ item, originalIndex }, index) => ({
      ...item,
      originalIndex,
      photo_url: getPhotoUrl(item.photo_key),
      tag: String(index + 1),
      item: item.title,
      qty: item.quantity,
      condition: item.condition_code,
      crate: "-",
      photos: item.photo_key ? 1 : 0,
    }));

  const renderCaptureTab = () => (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={12}>
          <Accordion
            defaultExpanded={false}
            disableGutters
            sx={{
              background: "#f5faf9",
              border: "1px solid #dfece8",
              borderRadius: "12px !important",
              boxShadow: "none",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="crew-inventory-job-details-content"
              id="crew-inventory-job-details-header"
              sx={{
                minHeight: 48,
                "& .MuiAccordionSummary-content": { my: 1 },
              }}
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: "#51656d" }}
              >
                Job details
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              id="crew-inventory-job-details-content"
              sx={{ pt: 0, px: { xs: 1.5, md: 2 }, pb: { xs: 1.5, md: 2 } }}
            >
              <Grid container spacing={0.8}>
                {inventoryMeta.map((item) => (
                  <Grid item xs={6} md={2} key={item.label}>
                    <Box
                      sx={{
                        background: "#fff",
                        border: "1px solid #edf1f3",
                        borderRadius: 2,
                        p: 1,
                        height: "100%",
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "#64748b", display: "block", mb: 0.3 }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#1f2937",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12} lg={12}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: 3,
              background: "#f9fbfc",
              border: "1px solid #e7edf1",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1}
              sx={{ mb: 1.4 }}
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: "#51656d" }}
              >
                Inventory entry form
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  isCrewSurveyItemsLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PlaylistAdd fontSize="small" />
                  )
                }
                disabled={
                  !inventoryId || isSubmitted || isCrewSurveyItemsLoading
                }
                onClick={handlePreloadSurveyItems}
                sx={{
                  textTransform: "none",
                  alignSelf: { xs: "flex-start", sm: "auto" },
                }}
              >
                {isCrewSurveyItemsLoading
                  ? "Loading survey items"
                  : "Preload survey items"}
              </Button>
            </Stack>

            <Grid container spacing={1.5}>
              {crewItemFormFields.map((field) => (
                <FormField
                  key={field.id}
                  {...field}
                  disabled={isSubmitted || field.disabled}
                  value={
                    itemFormData[field.id as keyof CrewInventoryItemFormData]
                  }
                  onChange={handleItemFieldChange}
                  error={Boolean(
                    formErrors[field.id as CrewInventoryRequiredField],
                  )}
                  // helperText={
                  //   formErrors[field.id as CrewInventoryRequiredField] || ""
                  // }
                  isNonMarginTop
                  inSingleGridForm
                  InputProps={{
                    sx: {
                      height: "36px",
                    },
                  }}
                />
              ))}
              <Grid item xs={12}>
                <Button
                  component="label"
                  fullWidth
                  disabled={
                    isSubmitted || crewInventoryPhotoUpload?.isUploading
                  }
                  variant="outlined"
                  startIcon={<PhotoCamera sx={{ fontSize: 18 }} />}
                  sx={{
                    justifyContent: "flex-start",
                    minHeight: 44,
                    borderStyle: "dashed",
                    borderColor: "#bfd5db",
                    background: "#f2f7fb",
                    color: "#4a6671",
                    textTransform: "none",
                  }}
                >
                  Add photos for item {itemFormData.ref_id}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={handleItemPhotoChange}
                  />
                </Button>
                {(itemPhoto || itemFormData.photo_key) && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 0.75 }}
                  >
                    <Typography variant="caption">
                      {itemPhoto
                        ? crewInventoryPhotoUpload?.isUploading
                          ? `Uploading ${itemPhoto.name}...`
                          : crewInventoryPhotoUpload?.hasError
                            ? `${itemPhoto.name} upload failed`
                            : itemFormData.photo_key
                              ? itemPhoto.name
                              : `${itemPhoto.name} upload pending`
                        : "Uploaded photo"}
                    </Typography>
                    {itemFormData.photo_key &&
                      getPhotoUrl(itemFormData.photo_key) && (
                        <Button
                          size="small"
                          startIcon={<VisibilityOutlined />}
                          onClick={() =>
                            handleViewPhoto(itemFormData.photo_key)
                          }
                        >
                          View photo
                        </Button>
                      )}
                    {itemFormData.photo_key && (
                      <IconButton
                        size="small"
                        aria-label="Remove uploaded photo"
                        disabled={
                          isSubmitted || crewInventoryPhotoUpload?.isUploading
                        }
                        onClick={handleRemoveItemPhoto}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                )}
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e5edf2" }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#51656d",
                  mb: 1.4,
                }}
              >
                Sign-off
              </Typography>
              <Grid container spacing={1.5}>
                {crewSignatureFields.map((field) => (
                  <FormField
                    key={field.id}
                    {...field}
                    disabled={isSubmitted || field.disabled}
                    value={
                      inventoryFormData[
                        field.id as "customer_sign" | "driver_sign_pickup"
                      ]
                    }
                    onChange={handleItemFieldChange}
                    error={Boolean(
                      formErrors[field.id as CrewInventoryRequiredField],
                    )}
                    isRequired={
                      field.id === "driver_sign_pickup" &&
                      Boolean(formErrors.driver_sign_pickup)
                    }
                    helperText={
                      formErrors[field.id as CrewInventoryRequiredField] || ""
                    }
                    isNonMarginTop
                    inSingleGridForm
                    InputProps={{
                      sx: {
                        height: "36px",
                      },
                    }}
                  />
                ))}
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.2}
        justifyContent="flex-end"
        sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #e5edf2" }}
      >
        {editingItem && (
          <Button
            variant="text"
            onClick={handleCancelItemEdit}
            sx={{ textTransform: "none" }}
          >
            Cancel edit
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={() => submitInventory("DRAFT")}
          disabled={isSubmitted || crewInventoryPhotoUpload?.isUploading}
          sx={{
            minWidth: { sm: 180 },
            borderRadius: 2,
            // borderColor: "#0d6f7f",
            // color: "#0b5f69",
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          {editingItem ? "Update item" : "Save as draft"}
        </Button>
        <Button
          variant="contained"
          onClick={() => submitInventory("SUBMITTED")}
          disabled={isSubmitted || crewInventoryPhotoUpload?.isUploading}
          sx={{
            minWidth: { sm: 180 },
            borderRadius: 2,
            // background: "linear-gradient(135deg, #0d6f7f 0%, #0a8098 100%)",
            fontWeight: 700,
            textTransform: "none",
            boxShadow: "none",
          }}
        >
          Submit & Lock
        </Button>
      </Stack>

      <Box sx={{ mt: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1.2 }}
        >
          <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
            Inventory items
          </Typography>
          <Typography variant="caption" sx={{ color: "#64748b" }}>
            {displayItems.length} items
          </Typography>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid #e5edf2",
            overflow: "hidden",
            background: "#fff",
          }}
        >
          <Box
            sx={{
              display: { xs: "none", md: "grid" },
              gridTemplateColumns: "0.7fr 2fr 1fr 1fr 0.8fr 1fr 0.8fr",
              background: "#026B56",
              color: "#fff",
              px: 2,
              py: 1.2,
              borderBottom: "1px solid #e6edf1",
            }}
          >
            {[
              ...CREW_ITEM_TABLE_HEADINGS,
              { label: "Actions", align: "center" as const },
            ].map((heading) => (
              <Typography
                key={heading.label}
                sx={{ fontWeight: 600 }}
                align={heading.align}
              >
                {heading.label}
              </Typography>
            ))}
          </Box>

          {displayItems.map((row: any, index: number) => (
            <Box
              key={`${row.tag}-${index}`}
              sx={{
                px: { xs: 1.5, md: 2 },
                py: { xs: 1, md: 1.2 },
                borderBottom:
                  index === clientInventoryCrewDetails?.items?.length - 1
                    ? "none"
                    : "1px solid #eef2f6",
                display: { xs: "block", md: "grid" },
                gridTemplateColumns: "0.7fr 2fr 1fr 1fr 0.8fr 1fr 0.8fr",
                background: index % 2 === 0 ? "#ffffff" : "#fafbfd",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.4,
                  borderBottom: { xs: "1px solid #eef2f6", md: "none" },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#64748b",
                    display: { xs: "block", md: "none" },
                  }}
                >
                  Tag
                </Typography>
                <Typography>{row.tag}</Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.4,
                  borderBottom: { xs: "1px solid #eef2f6", md: "none" },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#64748b",
                    display: { xs: "block", md: "none" },
                  }}
                >
                  Item
                </Typography>
                <Typography>{row.item}</Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.4,
                  borderBottom: { xs: "1px solid #eef2f6", md: "none" },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#64748b",
                    display: { xs: "block", md: "none" },
                  }}
                >
                  Qty
                </Typography>
                <Typography>{row.qty}</Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.4,
                  borderBottom: { xs: "1px solid #eef2f6", md: "none" },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#64748b",
                    display: { xs: "block", md: "none" },
                  }}
                >
                  Condition
                </Typography>
                <Typography>{row.condition}</Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.4,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#64748b",
                    display: { xs: "block", md: "none" },
                  }}
                >
                  Photos
                </Typography>
                <Stack direction="row" spacing={0.7} alignItems="center">
                  {/* <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                    {row.photos}
                    </Typography> */}
                  {row.photo_url && (
                    <>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          background: "#e8f4ef",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircle sx={{ fontSize: 14, color: "#1e9d7a" }} />
                      </Box>
                      <IconButton
                        size="small"
                        aria-label={`View photo for ${row.item}`}
                        onClick={() => handleViewPhoto(row.photo_key)}
                      >
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Stack>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-end", md: "center" },
                  alignItems: "center",
                  py: 0.4,
                }}
              >
                <IconButton
                  size="small"
                  aria-label={`Edit ${row.item}`}
                  disabled={isSubmitted}
                  onClick={() =>
                    handleEditItem(
                      inventoryFormData.items[row.originalIndex],
                      Number(row.tag),
                    )
                  }
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Paper>
      </Box>
    </Box>
  );

  const renderVerificationTab = () => (
    <Box
      aria-disabled={isCompleted}
      sx={{
        p: { xs: 1.5, md: 2.5 },
        ...(isCompleted && { pointerEvents: "none" }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#2e3d44" }}>
          Verification
        </Typography>
        {/* <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#687782" }}>
          14 / {clientInventoryCrewDetails?.items?.length || 0}
        </Typography> */}
      </Box>

      <Typography sx={{ fontSize: 12, color: "#5e6c75", mb: 1.2 }}>
        Tap a tile as each item is delivered • long-press to flag an exception
      </Typography>

      <Grid container spacing={0.8} sx={{ mb: 1.5 }}>
        {clientInventoryCrewDetails?.items?.map((item: any) => {
          const isVerified = item.status === "verified";
          const isException = item.status === "exception";
          const isNotChecked =
            item.status === "not-checked" || item.status === "PACKED";

          return (
            <Grid item xs={3} key={item.id}>
              <ButtonBase
                type="button"
                onClick={() => handleVerificationItemClick(item)}
                aria-label={`Edit notes for ${item.title}`}
                sx={{
                  width: "100%",
                  height: 76,
                  borderRadius: 1.5,
                  border: "1px solid #dfe8e2",
                  background: isException
                    ? "#f2d8a4"
                    : isNotChecked
                      ? "#f2f2f2"
                      : "#bfe0c7",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  color: isException
                    ? "#6f4d00"
                    : isNotChecked
                      ? "#8a8a8a"
                      : "#1f3a31",
                  px: 0.5,
                  fontWeight: 700,
                  boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.01)",
                }}
              >
                <Typography
                  sx={{ fontSize: 18, lineHeight: 1.1, fontWeight: 700 }}
                >
                  {item.ref_id}
                </Typography>
                <Typography sx={{ fontSize: 11, lineHeight: 1.1, mt: 0.3 }}>
                  {item.title}
                </Typography>
              </ButtonBase>
            </Grid>
          );
        })}
      </Grid>

      <Box
        sx={{
          display: "flex",
          gap: 1.4,
          alignItems: "center",
          flexWrap: "wrap",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 0.8,
              background: "#bfe0c7",
            }}
          />
          <Typography sx={{ fontSize: 12, color: "#4d5d63" }}>
            Verified
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 0.8,
              background: "#f2d8a4",
            }}
          />
          <Typography sx={{ fontSize: 12, color: "#4d5d63" }}>
            Exception
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 0.8,
              background: "#f2f2f2",
            }}
          />
          <Typography sx={{ fontSize: 12, color: "#4d5d63" }}>
            Not checked
          </Typography>
        </Box>
      </Box>

      {pendingVerificationItems.map((item, index) => {
        const itemIndex = inventoryFormData.items.findIndex(
          (inventoryItem) =>
            getInventoryItemKey(inventoryItem) === getInventoryItemKey(item),
        );
        const statusLabel =
          item.status === "verified"
            ? "Verified"
            : item.status === "exception"
              ? "Exception"
              : "Not checked";
        const isException = item.status === "exception";
        const isNotChecked = item.status === "not-checked";

        return (
          <Box
            key={getInventoryItemKey(item)}
            sx={{
              background: isException
                ? "#f2d8a4"
                : isNotChecked
                  ? "#f2f2f2"
                  : "#e8f4ef",
              borderRadius: 1.5,
              border: `1px solid ${isException ? "#e6c57d" : isNotChecked ? "#ddd" : "#b9daca"}`,
              p: 1.2,
              color: isException
                ? "#5d4300"
                : isNotChecked
                  ? "#64748b"
                  : "#1f3a31",
              fontWeight: 600,
              mb: 1.4,
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
              Tag {itemIndex >= 0 ? itemIndex + 1 : index + 1} – {item.title}
            </Typography>
            <Typography sx={{ fontSize: 12, mt: 0.3 }}>
              {statusLabel}
              {item.notes ? ` – ${item.notes}` : " – No notes added."}
              {item.photo_key ? " Photo attached." : ""}
            </Typography>
          </Box>
        );
      })}

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          variant="outlined"
          onClick={handleSaveProgress}
          disabled={isCompleted}
          sx={{
            flex: 1,
            borderRadius: 2,
            // borderColor: "#0d6f7f",
            // color: "#0b5f69",
            fontWeight: 700,
            textTransform: "none",
            py: 1.2,
            "&.Mui-disabled": { opacity: 1 },
          }}
        >
          Save progress
        </Button>
        <Button
          variant="contained"
          onClick={handleCompleteAndSign}
          disabled={isCompleted}
          sx={{
            flex: 1,
            borderRadius: 2,
            // background: "linear-gradient(135deg, #0d6f7f 0%, #0a8098 100%)",
            fontWeight: 700,
            textTransform: "none",
            boxShadow: "none",
            py: 1.2,
            "&.Mui-disabled": { opacity: 1 },
          }}
        >
          Complete + sign
        </Button>
      </Stack>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: {
          xs: "#eef3f5",
          md: "linear-gradient(180deg, #f5f7fa 0%, #edf4f7 100%)",
        },
        px: { xs: 1.5, md: 3 },
        py: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 3, md: 4 },
            overflow: "hidden",
            border: "1px solid #dfe7ec",
            background: "#fff",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #0c5b6d 0%, #0e7d8a 100%)",
              color: "#fff",
              px: { xs: 2, md: 3 },
              py: { xs: 2, md: 2.5 },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, letterSpacing: 0.1 }}
                >
                  Storage & Move Inventory
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {activeTab === "capture"
                    ? "Inventory capture"
                    : "Verification"}
                </Typography>
              </Box>
              <Tooltip title="Log out">
                <IconButton
                  aria-label="Log out"
                  onClick={handleLogout}
                  sx={{
                    width: 38,
                    height: 38,
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.35)",
                    borderRadius: 1.5,
                    "&:hover": { background: "rgba(255,255,255,0.14)" },
                  }}
                >
                  <LogoutOutlined sx={{ fontSize: 19 }} />
                </IconButton>
              </Tooltip>
            </Stack>

            <Tabs
              value={activeTab}
              onChange={(_, nextTab: "capture" | "verification") =>
                handleTabChange(nextTab)
              }
              variant="fullWidth"
              sx={{
                mt: 1.5,
                borderBottom: "1px solid rgba(255,255,255,0.2)",
                minHeight: 42,
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                  backgroundColor: "#a8e0cf",
                },
                "& .MuiTab-root": {
                  minHeight: 42,
                  color: "rgba(255,255,255,0.76)",
                  borderRadius: "8px 8px 0 0",
                  fontWeight: 700,
                  textTransform: "none",
                  "&.Mui-selected": {
                    color: "#fff",
                    backgroundColor: "rgba(255,255,255,0.12)",
                  },
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.38)" },
                },
              }}
            >
              <Tab label="Inventory capture" value="capture" />
              <Tab
                label="Verification"
                value="verification"
                disabled={!isSubmitted}
              />
            </Tabs>
          </Box>
          <Notification />
          {activeTab === "capture"
            ? renderCaptureTab()
            : renderVerificationTab()}
        </Paper>
      </Box>
      <CrewInventoryNoteModal
        open={Boolean(selectedVerificationItem)}
        item={selectedVerificationItem}
        driverSignDelivery={inventoryFormData.driver_sign_delivery}
        onClose={() => setSelectedVerificationItem(null)}
        onSave={handleVerificationNoteSave}
      />
      <AlertPopup
        open={Boolean(tabSwitchTarget)}
        title="Unsaved changes"
        content="Your changes on this tab have not been saved. Discard them and switch tabs?"
        yesText="Discard and switch"
        noText="Stay here"
        onHide={() => setTabSwitchTarget(null)}
        submitHandler={handleDiscardAndSwitchTab}
      />
    </Box>
  );
};

export default CrewInventoryPage;
